import { cn } from "@ronin/ui";
import { createContext, useContext } from "react";

export interface BoardsNav {
  boards: Array<{ id: string; name: string }>;
  activeId: string;
  select: (id: string) => void;
}

/** The boards and the open one, for the Boards widget (spec12 FR6). */
export const BoardsNavContext = createContext<BoardsNav | null>(null);

/**
 * spec12 FR6: one box per board, side by side; the open board has a lighter background
 * (`bg-muted`) and `text-foreground`.
 */
/** FR6: the boxes fill the tile; on a low tile (up to 1.5rem) the text is smaller. */
export function BoardsWidget({ live, h }: { live: boolean; h: number }) {
  const nav = useContext(BoardsNavContext);
  if (!nav) return null;
  return (
    <nav aria-label="Boards" className="flex h-full min-w-0 items-center gap-2">
      {nav.boards.map((board) => {
        const active = board.id === nav.activeId;
        const className = cn(
          "flex min-w-0 flex-1 items-center justify-center border px-3 font-medium transition-colors duration-150",
          "h-full",
          h <= 2 ? "text-xs" : "text-sm",
          active
            ? "border-input bg-muted text-foreground"
            : "border-border/60 bg-card/40 text-dim-foreground",
        );
        // In edit mode the tile opens its settings; the boxes are only a picture then.
        return live ? (
          <button
            key={board.id}
            type="button"
            aria-current={active ? "page" : undefined}
            onClick={() => nav.select(board.id)}
            className={cn(
              className,
              "hover:bg-muted hover:text-secondary-foreground focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
            )}
          >
            <span className="truncate">{board.name}</span>
          </button>
        ) : (
          <span key={board.id} className={className}>
            <span className="truncate">{board.name}</span>
          </span>
        );
      })}
    </nav>
  );
}
